import { func, bool, shape, string, number, object } from "prop-types"
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import StripeTestCards from "source/registrations/StripeTestCards"
import { useOrganizationFlag as useOrganizationFlagFromPayments } from "source/payments/hooks/useOrganization"
import { isEmpty } from "lodash/lang"
import moment from "moment-timezone"

const StripeForm = ({
  responsiblePerson,
  amount,
  onPaymentSave,
  onIsProcessing,
  onIsCaptureProcessing = () => {},
  onError,
  isTestMode = false,
  reservationExpiresAt,
  paymentIntent,
}) => {
  const savedPaymentsEnabled = useOrganizationFlagFromPayments(
    "useSavedPaymentMethods",
  )

  const showSavedPaymentMethods = savedPaymentsEnabled

  const stripe = useStripe()
  const elements = useElements()

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (
      reservationExpiresAt &&
      moment(reservationExpiresAt).isBefore(moment())
    ) {
      return onError("Reservation has expired")
    }

    if (amount < 50) {
      return onError("Amount must be greater than $0.50")
    }

    const { error: elementError } = await elements.fetchUpdates()

    if (elementError) {
      return onError(elementError.message)
    }

    onIsProcessing(true)

    let payload = {
      redirect: "if_required",
      confirmParams: {
        payment_method_data: {
          billing_details: {
            name: responsiblePerson.name,
            email: responsiblePerson.emailAddress,
          },
        },
      },
    }
    if (!isEmpty(paymentIntent?.paymentMethod)) {
      payload.payment_method = paymentIntent.paymentMethod
      payload.clientSecret = paymentIntent.clientSecret
      payload.customer = paymentIntent.customer
      if (showSavedPaymentMethods) {
        payload.customerSessionClientSecret =
          paymentIntent.customerSessionClientSecret
      }
    } else {
      payload.elements = elements
    }

    const { error, paymentIntent: response } = await stripe.confirmPayment({
      ...payload,
    })

    if (error) {
      onError(error.message)
    } else {
      onError(null)

      await onPaymentSave(response)
    }

    onIsProcessing(false)
  }

  const options = {
    fields: {
      billingDetails: {
        name: "never",
        email: "never",
      },
    },
    layout: {
      type: "accordion",
      defaultCollapsed: false,
      radios: true,
      spacedAccordionItems: false,
    },
    paymentMethodOrder: ["apple_pay", "google_pay", "card"],
    wallets: {
      applePay: "auto",
      googlePay: "auto",
    },
  }

  const cardOnlyOptions = {
    fields: {
      billingDetails: {
        name: "never",
        email: "never",
      },
    },
    layout: {
      type: "tabs",
      defaultCollapsed: false,
    },
    paymentMethodOrder: ["apple_pay", "google_pay", "card"],
    wallets: {
      applePay: "never",
      googlePay: "never",
    },
  }

  return (
    <div>
      {isTestMode && <StripeTestCards />}
      <form id="paymentForm" className="ta-c" onSubmit={handleSubmit}>
        <PaymentElement
          options={showSavedPaymentMethods ? cardOnlyOptions : options}
        />
      </form>
    </div>
  )
}

StripeForm.propTypes = {
  responsiblePerson: shape({
    name: string.isRequired,
    emailAddress: string.isRequired,
  }).isRequired,
  amount: number,
  onPaymentSave: func.isRequired,
  onIsProcessing: func.isRequired,
  onIsCaptureProcessing: func,
  onError: func.isRequired,
  isTestMode: bool,
  reservationExpiresAt: string,
  paymentIntent: object,
}

export default StripeForm
